import { Service, inject } from '@piwikpro/platform';
import { createAction } from 'redux-actions';
import type { Store } from 'redux';
import { HttpClient } from '@piwikpro/http-crate';
import {
  NotifyAboutRemoval,
  NotifyOnFail,
  NotifyAboutUpdate,
  NotifyAboutCreation,
} from '@piwikpro/notification-crate';
import { Confirmation } from '@piwikpro/confirmation-crate';
import confirmationModalBody from './components/Confirmation/RemoveDomainConfirmation';
import { DomainsListResponse, DomainTypes } from './redux/list';
import { DefaultDomainsResponse } from './redux/defaults';

export const domainsListFetchInitialized = createAction(
  '@DomainsService/query/domainsList/get/init',
);
export const domainsListFetchSucceeded = createAction<
DomainsListResponse, DomainsListResponse
>(
  '@DomainsService/query/domainsList/get/success',
  payload => payload,
);
export const domainsListFetchFailed = createAction(
  '@DomainsService/query/domainsList/get/failed',
);
export const defaultDomainsFetchInitialized = createAction(
  '@DomainsService/query/defaultDomains/get/init',
);
export const defaultDomainsFetchSucceeded = createAction<
DefaultDomainsResponse, DefaultDomainsResponse
>(
  '@DomainsService/query/defaultDomains/get/success',
  payload => payload,
);
export const defaultDomainsFetchFailed = createAction(
  '@DomainsService/query/defaultDomains/get/failed',
);
export const defaultDomainsSetInitialized = createAction(
  '@DomainsService/query/defaultDomains/set/init',
);
export const defaultDomainsSetSucceeded = createAction(
  '@DomainsService/query/defaultDomains/set/success',
);
export const defaultDomainsSetFailed = createAction(
  '@DomainsService/query/defaultDomains/set/failed',
);

@Service()
export class DomainsService {
  constructor(
    @inject('store') private readonly store: Store,
    @inject('HttpCrate.httpClient') private readonly httpClient: HttpClient,
    @inject('ConfirmationCrate.confirmation') private readonly confirmation: Confirmation,
    @inject('NotificationCrate.interceptors.NotifyOnFail') private readonly notifyOnFail: NotifyOnFail,
    @inject('NotificationCrate.interceptors.NotifyAboutRemoval') private readonly notifyAboutRemoval: NotifyAboutRemoval,
    @inject('NotificationCrate.interceptors.NotifyAboutUpdate') private readonly notifyAboutUpdate: NotifyAboutUpdate,
    @inject('NotificationCrate.interceptors.NotifyAboutCreation') private readonly notifyAboutCreation: NotifyAboutCreation,
    @inject('config') private readonly config: any,
  ) { }

  async getDomainsList({ refetchCertStatusOfDomain }: { refetchCertStatusOfDomain?: string } = {}) {
    if (!refetchCertStatusOfDomain) {
      this.store.dispatch(domainsListFetchInitialized());
    }

    try {
      const response: DomainsListResponse = await this.httpClient.request({
        endpoint: `${this.config.get('DOMAINS_URL')}`,
        method: 'GET',
        retryInterval: 1500,
        retryNumber: 5,
        retryCondition: async (res) => {
          if (!res.ok) {
            return true;
          }

          if (refetchCertStatusOfDomain) {
            const responseBody: DomainsListResponse = await res.clone().json();

            return responseBody.some(domain => domain.domain === refetchCertStatusOfDomain && domain.certificate.status.name === 'processing');
          }

          return false;
        },
      })
        .intercept(this.notifyOnFail)
        .jsonApi()
        .send();

      this.store.dispatch(domainsListFetchSucceeded(response));

      return response;
    } catch (err: any) {
      this.store.dispatch(domainsListFetchFailed());

      throw err;
    }
  }

  async getDefaultDomains() {
    this.store.dispatch(defaultDomainsFetchInitialized());

    try {
      const response: DefaultDomainsResponse = await this.httpClient.request({
        endpoint: `${this.config.get('DOMAINS_URL')}/defaults`,
        method: 'GET',
      })
        .intercept(this.notifyOnFail)
        .json()
        .send();

      this.store.dispatch(defaultDomainsFetchSucceeded(response));

      return response;
    } catch (err: any) {
      this.store.dispatch(defaultDomainsFetchFailed());

      throw err;
    }
  }

  async setDefaultDomains(
    defaultDomains: {
      staticResources: string
      trackingDynamicEndpoints: string
      uiApis: string
    }, withConfirmation?: boolean,
  ): Promise<DefaultDomainsResponse | void> {
    const request = async () => {
      try {
        const response: DefaultDomainsResponse = await this.httpClient.request({
          endpoint: `${this.config.get('DOMAINS_URL')}/defaults`,
          body: {
            static_resources: defaultDomains.staticResources,
            tracking_dynamic_endpoints: defaultDomains.trackingDynamicEndpoints,
            ui_apis: defaultDomains.uiApis,
          },
          method: 'PATCH',
        })
          .intercept(this.notifyAboutUpdate)
          .json()
          .send();

        this.store.dispatch(defaultDomainsSetSucceeded());

        return response;
      } catch (err: any) {
        this.store.dispatch(defaultDomainsSetFailed());

        throw err;
      }
    };

    this.store.dispatch(defaultDomainsSetInitialized());

    if (withConfirmation) {
      await this.confirmation.confirm({
        title: 'administration:platform.domains.static-resources-change-confirmation.header',
        messages: [
          'administration:platform.domains.static-resources-change-confirmation.description-line-1',
          'administration:platform.domains.static-resources-change-confirmation.description-line-2',
        ],
        applyBtnText: 'administration:platform.domains.static-resources-change-confirmation.apply-button',
        asyncAction: async () => {
          await request();
        },
      });
      return;
    }

    await request();
  }

  async addDomain(
    { name, types, certificate }:
    { name: string, types: DomainTypes[], certificate: { key: string, chain: string } },
  ) {
    try {
      await this.httpClient.request({
        endpoint: `${this.config.get('DOMAINS_URL')}`,
        method: 'POST',
        body: {
          domain: name,
          types,
          certificate,
        },
      })
        .intercept(this.notifyAboutCreation)
        .json()
        .send();
    } catch (err: any) {
      throw err;
    }
  }

  async deleteDomain({
    name, showSamlMessage,
  }: {
    name: string
    showSamlMessage?: boolean
  }) {
    await this.confirmation.confirm({
      name,
      title: 'administration:platform.domains.removeDomain.modal.title',
      applyBtnText: 'administration:platform.domains.domains-list.remove-option',
      type: 'customBody',
      asyncAction: async () => {
        await this.httpClient.request({
          endpoint: `${this.config.get('DOMAINS_URL')}/${name}`,
          method: 'DELETE',
        }).intercept(this.notifyAboutRemoval).send();
      },
      // @ts-ignore:next-line
    }, () => confirmationModalBody({ showSamlMessage }));
  }

  async updateDomain(
    { name, types, certificate }:
    { name: string, types?: DomainTypes[], certificate?: { key: string, chain: string } },
  ) {
    try {
      await this.httpClient.request({
        endpoint: `${this.config.get('DOMAINS_URL')}/${name}`,
        method: 'PATCH',
        body: {
          types,
          certificate,
        },
      })
        .intercept(this.notifyAboutUpdate)
        .json()
        .send();
    } catch (err: any) {
      throw err;
    }
  }
}
